import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandList,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";
import { ChevronDown, User, Check } from "lucide-react";
import { getTelephonyUsers } from "../../../apis/phoneInventory";
import { getMembers } from "../../../apis/workspace/members";

function VirtualUserSingleSelect({
    selectedId = null,
    onChange,
    disabled = false,
    placeholder = "Select user",
    emptyMessage = "No users found",
    errorMessage = "Failed to load users",
    className,
    triggerClassName,
    workspaceId,
    preloadedUsers = [],
    userType = "members", // "members" or "telephony"
    excludeUserIds = [], // Array of user IDs to exclude from the list
}) {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [hasFetched, setHasFetched] = useState(false);
    const [selectedUserData, setSelectedUserData] = useState(null); // Store selected user's full data

    const parentRef = useRef(null);
    const triggerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // Debounce search query
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setPage(1);
            setUsers([]);
            setHasMore(true);
            setHasFetched(false);
        }, 300);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchQuery]);

    // Fetch users from API
    const fetchUsers = useCallback(
        async (pageNum, search) => {
            if (!workspaceId) return;

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            setLoading(true);
            setError(false);

            try {
                const queryParams = new URLSearchParams({
                    page: pageNum.toString(),
                    per_page: "20",
                });

                if (search) {
                    const filterParam = userType === "telephony" ? "filter[user_name]" : "filter[name]";
                    queryParams.append(filterParam, search);
                }

                const fetchFn = userType === "telephony" ? getTelephonyUsers : getMembers;
                const response = await fetchFn({
                    query: queryParams.toString(),
                    options: {
                        headers: { "X-Workspace-Id": workspaceId },
                        signal: abortControllerRef.current.signal,
                    },
                });

                const newUsers = response?.data?.data || response?.data || [];
                const pagination = response?.data?.pagination || {};

                setUsers((prev) => (pageNum === 1 ? newUsers : [...prev, ...newUsers]));
                setHasMore(pagination.current_page < pagination.total_pages || newUsers.length === 20);
                setHasFetched(true);
                setLoading(false);
            } catch (err) {
                if (err.name !== "AbortError" && err.name !== "CanceledError") {
                    console.error("Error fetching users:", err);
                    setError(true);
                    setHasFetched(true);
                    setLoading(false);
                }
            }
        },
        [workspaceId, userType]
    );

    useEffect(() => {
        if (open && !hasFetched && !loading) {
            fetchUsers(1, debouncedSearchQuery);
        }
    }, [open, hasFetched, loading, fetchUsers, debouncedSearchQuery]);

    const loadMore = useCallback(() => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchUsers(nextPage, debouncedSearchQuery);
        }
    }, [loading, hasMore, page, fetchUsers, debouncedSearchQuery]);

    const rowVirtualizer = useVirtualizer({
        count: users.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 56,
        overscan: 5,
    });

    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

        if (!lastItem) return;

        if (lastItem.index >= users.length - 1 && hasMore && !loading) {
            loadMore();
        }
    }, [rowVirtualizer.getVirtualItems(), users.length, hasMore, loading, loadMore]);

    useEffect(() => {
        if (disabled && open) {
            setOpen(false);
        }
    }, [disabled, open]);

    // Filter out excluded users
    const filteredUsers = useMemo(() => {
        if (!excludeUserIds || excludeUserIds.length === 0) {
            return users;
        }
        const excludeSet = new Set(excludeUserIds);
        return users.filter((user) => !excludeSet.has(user.id) && !excludeSet.has(user.user_id));
    }, [users, excludeUserIds]);

    const usersById = useMemo(() => {
        const map = new Map();

        preloadedUsers.forEach((user) => {
            if (user && user.id) {
                map.set(user.id, user);
            }
        });

        filteredUsers.forEach((user) => {
            if (user && user.id) {
                map.set(user.id, user);
            }
        });

        return map;
    }, [filteredUsers, preloadedUsers]);

    const selectedUser = useMemo(() => {
        if (!selectedId) return null;
        // First check if we have stored user data
        if (selectedUserData && selectedUserData.id === selectedId) {
            return selectedUserData;
        }
        // Then check usersById map
        if (usersById.has(selectedId)) {
            return usersById.get(selectedId);
        }
       // Fallback to showing ID
        return { id: selectedId, name: selectedId, email: null, isPlaceholder: true };
    }, [selectedId, usersById, selectedUserData]);

    // Sync selectedUserData when user is found 
    useEffect(() => {
        if (selectedId && usersById.has(selectedId)) {
            if (!selectedUserData || selectedUserData.id !== selectedId) {
                setSelectedUserData(usersById.get(selectedId));
            }
        }
    }, [selectedId, selectedUserData, usersById]);

    const handleSelect = (user) => {
        if (!onChange) return;
        setSelectedUserData(user); // Store full user data
        onChange(user.id, user); 
        setOpen(false);
    };

    const handleOpenChange = (nextOpen) => {
        if (disabled) return;
        setOpen(nextOpen);
        if (!nextOpen) {
            setSearchQuery("");
            setHasFetched(false);
        }
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    className={cn(
                        "flex w-full h-12 items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-left text-sm transition focus:outline-none",
                        disabled ? "cursor-not-allowed bg-gray-50 text-gray-400" : "hover:border-[#D0D5DD]",
                        triggerClassName,
                        className
                    )}
                    disabled={disabled}
                >
                    <div className="inline-flex items-center space-x-2.5">
                        <User className="h-4 w-4 text-gray-500" />
                        {selectedUser ? (
                            selectedUser.isPlaceholder ? (
                                <span className="text-sm text-gray-500">Loading user data...</span>
                            ) : (
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900">
                                        {selectedUser.name || selectedUser.email || selectedUser.id}
                                    </span>
                                    {selectedUser.email && selectedUser.name && (
                                        <span className="text-xs text-gray-500">{selectedUser.email}</span>
                                    )}
                                </div>
                            )
                        ) : (
                            <span className="text-sm text-gray-500">
                                {loading ? "Loading users..." : placeholder}
                            </span>
                        )}
                    </div>

                    <div className="ml-2 flex items-center gap-2 text-gray-500">
                        {loading && <div className="h-3 w-3 animate-spin rounded-full border-2 border-purple-200 border-t-purple-600" />}
                        <ChevronDown className="h-4 w-4" />
                    </div>
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0"
                align="start"
                style={{ width: triggerRef.current?.offsetWidth || 320 }}
            >
                <Command shouldFilter={false}>
                    <div className="p-2">
                        <div className="rounded-md border border-border">
                            <CommandInput
                                placeholder="Search users"
                                className="h-9"
                                value={searchQuery}
                                onValueChange={handleSearchChange}
                            />
                        </div>
                    </div>
                    <CommandList>
                        <div
                            ref={parentRef}
                            className="max-h-[300px] overflow-auto"
                        >
                            {error ? (
                                <div className="px-4 py-6 text-sm text-red-500">{errorMessage}</div>
                            ) : filteredUsers.length === 0 && !loading ? (
                                <CommandEmpty className="py-6 text-center text-sm text-gray-500">
                                    {emptyMessage}
                                </CommandEmpty>
                            ) : (
                                <CommandGroup>
                                    <div
                                        style={{
                                            height: `${rowVirtualizer.getTotalSize()}px`,
                                            width: "100%",
                                            position: "relative",
                                        }}
                                    >
                                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                            const user = filteredUsers[virtualRow.index];
                                            if (!user || !user.id) return null;

                                            const isSelected = selectedId === user.id;

                                            return (
                                                <div
                                                    key={user.id}
                                                    data-index={virtualRow.index}
                                                    ref={rowVirtualizer.measureElement}
                                                    style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        width: "100%",
                                                        transform: `translateY(${virtualRow.start}px)`,
                                                    }}
                                                    className={cn(
                                                        "flex items-center justify-between gap-3 px-3 py-2 hover:bg-accent cursor-pointer",
                                                        isSelected && "bg-purple-50"
                                                    )}
                                                    onClick={() => handleSelect(user)}
                                                >
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium text-gray-950">
                                                            {user.name || user.email || user.id}
                                                        </span>
                                                        {user.email && user.name && (
                                                            <span className="text-xs text-gray-500">{user.email}</span>
                                                        )}
                                                    </div>
                                                    {isSelected && (
                                                        <Check className="h-4 w-4 text-purple-600" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CommandGroup>
                            )}
                            {loading && filteredUsers.length > 0 && (
                                <div className="py-3 text-center text-sm text-gray-500">
                                    Loading more...
                                </div>
                            )}
                            {!hasMore && filteredUsers.length > 0 && !loading && (
                                <div className="py-2 text-center text-xs text-gray-400">
                                    No more users
                                </div>
                            )}
                        </div>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default VirtualUserSingleSelect;
